import React, { useState, useEffect } from 'react';
import api from '../api';
import AuthImg from './AuthImg';

// ── The welcome scene ───────────────────────────────────────────────────────
// Shown once per sign-in to IT staff, and to nobody else.
//
// The artwork is drawn here rather than loaded as a GIF. That is not a saving
// measure: a GIF plays the same loop whatever is happening, and this scene
// reads the actual numbers — sticky notes multiply, the coffee stops steaming,
// the posture sags, and a clear board gets confetti. It is a few KB, stays
// sharp at any size, and the server does no work to show it.
//
// Everything animates in CSS. Nothing here polls, and the one request it makes
// is four indexed counts.

// The "seen" flag is keyed by WHO is signed in. A single key meant that after
// one person saw it, anybody who signed in next in the same browser tab saw
// nothing — which is precisely what happens while testing, and what happens on
// a shared machine in a campus IT room.
const seenKey = userId => `welcome_seen_${userId || 'anon'}`;

// Keyframes are injected once, not per instance — a component mounted on two
// pages must not install the same rules twice.
const STYLE_ID = 'welcome-scene-keyframes';
const CSS = `
@keyframes wsSweep{0%{transform:translateX(-26px);opacity:0}100%{transform:translateX(0);opacity:1}}
@keyframes wsBlink{0%,94%{transform:scaleY(1)}96%,98%{transform:scaleY(.08)}100%{transform:scaleY(1)}}
@keyframes wsSteam{0%{opacity:0;transform:translateY(2px)}35%{opacity:.6}100%{opacity:0;transform:translateY(-18px)}}
@keyframes wsSway{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(3deg)}}
@keyframes wsBreathe{0%,100%{transform:translateY(0)}50%{transform:translateY(-2.5px)}}
@keyframes wsSlump{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(3px) rotate(-1.5deg)}}
@keyframes wsType{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
@keyframes wsScan{0%{transform:translateY(0);opacity:0}20%{opacity:.5}100%{transform:translateY(42px);opacity:0}}
@keyframes wsPop{0%{transform:scale(.4);opacity:0}70%{transform:scale(1.12);opacity:1}100%{transform:scale(1);opacity:1}}
@keyframes wsWave{0%,100%{transform:rotate(0)}25%{transform:rotate(-24deg)}75%{transform:rotate(16deg)}}
@keyframes wsConfetti{0%{transform:translateY(-6px) rotate(0);opacity:0}15%{opacity:1}100%{transform:translateY(104px) rotate(260deg);opacity:0}}
@keyframes wsZzz{0%{transform:translate(0,0) scale(.7);opacity:0}30%{opacity:.85}100%{transform:translate(14px,-26px) scale(1.15);opacity:0}}
@keyframes wsLed{0%,100%{opacity:.25}50%{opacity:1}}
@media (prefers-reduced-motion: reduce){
  .ws-scene *,.ws-banner{animation:none !important}
}`;

function useKeyframes() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

const DAY = {
  bg: 'linear-gradient(90deg,#fff6e3 0%,#ffffff 64%)', edge: '#eed9ab',
  artBg: '#fffaf0', wall: '#fff6e3', windowFill: '#ffe2a8',
  desk: '#f4e5c8', deskEdge: '#e3cfa6', line: '#7a5410', mon: '#ffffff',
  screen: '#ffeec9', shirt: '#0e1b47', skin: '#f8e6cf', hair: '#2a2118',
  pot: '#c2764a', leaf: '#5d9c6a', rack: '#fff3dd', cta: '#0e1b47',
};
const DUSK = {
  bg: 'linear-gradient(90deg,#e9ecf7 0%,#ffffff 64%)', edge: '#c3cddf',
  artBg: '#eef1fa', wall: '#e7ebf6', windowFill: '#39456b',
  desk: '#d8def0', deskEdge: '#c3cddf', line: '#39456b', mon: '#f6f8fd',
  screen: '#cfd8ef', shirt: '#39456b', skin: '#f6ead9', hair: '#2c3550',
  pot: '#c2a07c', leaf: '#6f7fa8', rack: '#e3e8f5', cta: '#39456b',
};

const MOUTH = {
  smile: 'M97 97 q11 11 22 0',
  grin:  'M98 97 q10 7 20 0',
  flat:  'M99 98 h18',
  frown: 'M98 100 q10 -7 20 0',
};

// Several lines per mood, picked by the date. One line per state would be
// read twice and ignored thereafter.
const LINES = {
  morning: {
    clear: ['Nothing pending. Genuinely nothing. Enjoy it while it lasts.',
            'An empty board to start the day. Rare and well earned.',
            'Nothing waiting. Today is yours to get ahead on.'],
    steady: ['A light board today. Clear these and the day is yours.',
             'A handful waiting. Nothing that will spoil the morning.',
             'Only a few open. A good morning to get ahead.'],
    busy: ['A full tray this morning. Start at the top and work down.',
           'Plenty waiting. Pick the oldest first — they age badly.',
           'A busy board. Clear the critical ones and the rest will follow.'],
    swamped: ['Deep breath. Start with the oldest and the rest will follow.',
              'More than a day’s worth waiting. Triage first, tidy later.',
              'A heavy board this morning. Ask for a hand if you need one.'],
  },
  evening: {
    clear: ['Everything closed. That is a rare screen — go home.',
            'Nothing left open. A clean finish to the day.',
            'All clear. Shut the laptop with a straight face.'],
    steady: ['Almost there. A couple left before you call it a day.',
             'Nearly clear. Two minutes of tidying and you are done.',
             'A short list left. Finish these and close down.'],
    busy: ['Still pending. These will not close themselves. Unfortunately.',
           'A few of these have been open all day. One more push.',
           'Still a tray full. Pick off what you can before you go.'],
    swamped: ['Even the coffee has given up waiting. Tomorrow starts here.',
              'Too many to finish tonight. Note where you got to.',
              'A long day. Leave a note on the oldest ones for the morning.'],
  },
};

const POKE = {
  clear: 'Told you — nothing left!',
  steady: 'On it.',
  busy: 'Working on it.',
  swamped: 'I know. I know.',
};

const pick = arr => arr[new Date().getDate() % arr.length];

// Who sees it is decided by the SERVER (the endpoint answers "not applicable"
// for anyone who is not IT), so this component carries no role test of its own
// that could drift from it.
export default function WelcomeScene({ go }) {
  useKeyframes();
  const [d, setD] = useState(null);
  const [shown, setShown] = useState(false);
  const [poked, setPoked] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    // Once per sign-in, per person. sessionStorage dies with the tab's session,
    // which is what "per login" means in practice — and it keeps a refresh
    // from replaying the banner in the middle of a task.
    let userId = null;
    try { userId = JSON.parse(localStorage.getItem('user') || '{}')?.id || null; } catch { /* ignore */ }
    const key = seenKey(userId);
    let seen = false;
    try { seen = !!sessionStorage.getItem(key); } catch { seen = false; }
    if (seen) return;
    api.get('/me/welcome')
      .then(r => {
        if (!r.data?.applicable) return;
        setD(r.data);
        setShown(true);
        try { sessionStorage.setItem(key, String(Date.now())); } catch { /* private mode */ }
      })
      .catch(() => {});
  }, []);

  if (!shown || !d) return null;

  // Evening from 4pm; the small hours count as evening too, for anyone still on.
  const evening = d.hour >= 16 || d.hour < 5;
  const p = evening ? DUSK : DAY;
  const open = d.open_calls || 0;
  const critical = d.critical || 0;

  const mood = open === 0 ? 'clear' : open <= 5 ? 'steady' : open <= 12 ? 'busy' : 'swamped';
  const isClear = mood === 'clear';
  const isSwamped = mood === 'swamped';
  // A live critical outranks the hour and the pile: it is a different day.
  const incident = critical > 0 && !isClear;

  const mouth = isClear ? MOUTH.smile : mood === 'steady' ? MOUTH.grin
    : isSwamped ? MOUTH.frown : MOUTH.flat;
  const bodyAnim = isSwamped ? 'wsSlump 4s ease-in-out infinite'
    : isClear ? 'wsBreathe 2.6s ease-in-out infinite'
    : 'wsBreathe 4s ease-in-out infinite';

  const notes = [];
  const stickyCols = ['#ffd98a', '#ffc9c9', '#c7e7cf', '#cfd8ef'];
  for (let i = 0; i < Math.min(Math.ceil(open / 2), 8); i++) {
    notes.push({ x: 18 + (i % 4) * 24, y: 14 + Math.floor(i / 4) * 24,
                 fill: stickyCols[i % stickyCols.length], delay: i * 0.06 });
  }

  const greeting = `Good ${evening ? 'evening' : 'morning'}, ${d.first_name}`;
  const line = pick(LINES[evening ? 'evening' : 'morning'][mood]);

  const cards = [
    ['Pending calls', open, open ? 'oldest first' : 'nothing waiting', '#0e1b47', 'tasks'],
    ['Critical pending', critical, critical ? 'needs attention now' : 'none', '#dc2626', 'tasks'],
    ['Rooms without assets', d.rooms_without_assets || 0, 'nothing recorded yet', '#d97706', 'rooms'],
    ['Policies not accepted', d.policies_pending || 0, 'still to be signed', '#7c3aed', 'policies'],
  ];

  const close = () => setShown(false);

  return (
    <div className="ws-banner" style={{
      background: p.bg, border: `1px solid ${p.edge}`, borderRadius: 14,
      padding: '20px 22px', marginBottom: 20,
      boxShadow: '0 2px 10px rgba(14,27,71,.06)',
      animation: 'wsSweep .5s cubic-bezier(.22,.8,.3,1) both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>

        <button type="button" className="ws-scene" onClick={() => {
            setPoked(true); setTimeout(() => setPoked(false), 1800);
          }}
          aria-label="Say hello"
          style={{ width: 300, height: 190, flex: '0 0 auto', padding: 0, cursor: 'pointer',
                   borderRadius: 14, background: p.artBg, position: 'relative',
                   overflow: 'hidden', border: `1px solid ${p.edge}` }}>

          {/* An uploaded picture takes this slot when one has been set for the
              time of day and mood. If it fails to load — deleted, corrupt,
              wrong format — onError drops it and the drawn scene below shows
              instead, so the greeting never appears broken. */}
          {/* AuthImg, not a plain <img>: /api/master/welcome-images/:id/file
              sits behind the auth middleware, and a tag cannot send the token
              header — so an uploaded picture silently 401'd and fell back to
              the drawn scene every time. */}
          {d.image_url && !imgFailed ? (
            <AuthImg src={d.image_url.replace(/^\/api/, '')} alt=""
                 fallback={null}
                 style={{ display: 'block', width: '100%', height: '100%',
                          objectFit: 'cover' }} />
          ) : (
          <svg viewBox="0 0 300 190" width="300" height="190" aria-hidden="true"
               style={{ display: 'block' }}>
            <rect x="0" y="0" width="300" height="146" fill={p.wall} />

            {incident ? (
              <g>
                <rect x="214" y="12" width="72" height="124" rx="5" fill={p.rack}
                      stroke={p.line} strokeWidth="2.4" />
                {[22, 44, 66, 88].map(y => (
                  <rect key={y} x="222" y={y} width="56" height="16" rx="2" fill={p.screen} />
                ))}
                {[[30, '#dc2626', 1.1, 0], [52, '#16a34a', 1.7, .3],
                  [74, '#dc2626', .9, .6], [96, '#16a34a', 2.1, .2]].map(([cy, c, dur, del]) => (
                  <circle key={cy} cx="270" cy={cy} r="3" fill={c}
                          style={{ animation: `wsLed ${dur}s ease-in-out ${del}s infinite` }} />
                ))}
              </g>
            ) : (
              <g>
                <rect x="214" y="16" width="70" height="52" rx="4" fill={p.windowFill} opacity=".9" />
                <path d="M214 42 h70 M249 16 v52" stroke={p.edge} strokeWidth="2" />
              </g>
            )}

            {notes.map((n, i) => (
              <rect key={i} x={n.x} y={n.y} width="17" height="17" rx="2" fill={n.fill}
                    style={{ animation: `wsPop .4s ease-out ${n.delay}s both` }} />
            ))}

            <rect x="0" y="146" width="300" height="44" fill={p.desk} />
            <rect x="0" y="146" width="300" height="4" fill={p.deskEdge} />

            {!incident && (
              <g>
                <rect x="176" y="78" width="96" height="62" rx="6" fill={p.mon}
                      stroke={p.line} strokeWidth="2.5" />
                <rect x="184" y="86" width="80" height="46" rx="3" fill={p.screen} />
                <rect x="184" y="86" width="80" height="8" fill="#fff" opacity="0"
                      style={{ animation: 'wsScan 2.6s linear infinite' }} />
                <rect x="190" y="94" width="38" height="3.5" rx="1.75" fill={p.line} opacity=".45" />
                <rect x="190" y="103" width="56" height="3.5" rx="1.75" fill={p.line} opacity=".3" />
                <rect x="214" y="140" width="20" height="6" fill={p.line} />
                <rect x="204" y="146" width="40" height="4" rx="2" fill={p.line} />
              </g>
            )}

            <g style={{ transformOrigin: '108px 146px', animation: bodyAnim }}>
              <path d="M74 146 q0 -36 34 -36 q34 0 34 36" fill={p.shirt} />
              <text x="108" y="138" textAnchor="middle" fontFamily="DM Sans, sans-serif"
                    fontSize="11" fontWeight="700" fill="#ffffff">IT</text>
              <circle cx="108" cy="86" r="25" fill={p.skin} stroke={p.line} strokeWidth="2.5" />
              <path d="M85 78 q6 -18 23 -18 q17 0 23 18 q-10 -7 -23 -7 q-13 0 -23 7" fill={p.hair} />
              <g style={{ transformOrigin: '99px 84px', animation: 'wsBlink 5.5s ease-in-out infinite' }}>
                <circle cx="99" cy="84" r="2.8" fill={p.line} />
              </g>
              <g style={{ transformOrigin: '117px 84px', animation: 'wsBlink 5.5s ease-in-out infinite' }}>
                <circle cx="117" cy="84" r="2.8" fill={p.line} />
              </g>
              <path d={mouth} fill="none" stroke={p.line} strokeWidth="2.6" strokeLinecap="round" />
              <g style={{ transformOrigin: '138px 128px',
                          animation: poked ? 'wsWave .5s ease-in-out 2'
                                           : 'wsType 1.1s ease-in-out infinite' }}>
                <path d="M138 128 q22 -4 30 8" fill="none" stroke={p.line}
                      strokeWidth="2.6" strokeLinecap="round" />
              </g>
            </g>

            <g>
              <rect x="34" y="120" width="26" height="24" rx="3" fill="#fff"
                    stroke={p.line} strokeWidth="2.4" />
              <path d="M60 126 q9 6 0 12" fill="none" stroke={p.line} strokeWidth="2.4" />
              {!isSwamped && (
                <g>
                  <path d="M41 116 q5 -9 0 -16" fill="none" stroke={p.line} strokeWidth="2"
                        opacity="0" style={{ animation: 'wsSteam 3.2s ease-out infinite' }} />
                  <path d="M52 116 q5 -9 0 -16" fill="none" stroke={p.line} strokeWidth="2"
                        opacity="0" style={{ animation: 'wsSteam 3.2s ease-out 1.1s infinite' }} />
                </g>
              )}
            </g>

            <g style={{ transformOrigin: '282px 146px', animation: 'wsSway 6s ease-in-out infinite' }}>
              <rect x="274" y="132" width="17" height="14" rx="2" fill={p.pot} />
              <path d="M282 132 v-16" stroke={p.line} strokeWidth="2.4" />
              <path d="M282 122 q-14 -5 -12 -19 q14 3 12 19" fill={p.leaf} />
            </g>

            {isClear && (
              <g>
                {[[60, 10, '#16a34a', 2.6, 0], [120, 4, '#d97706', 2.9, .4],
                  [178, 12, '#7c3aed', 3.1, .9]].map(([x, y, c, dur, del]) => (
                  <rect key={x} x={x} y={y} width="6" height="9" fill={c}
                        style={{ animation: `wsConfetti ${dur}s linear ${del}s infinite` }} />
                ))}
              </g>
            )}

            {isSwamped && (
              <g>
                <text x="136" y="60" fontFamily="DM Sans, sans-serif" fontSize="13"
                      fontWeight="700" fill={p.line} opacity="0"
                      style={{ animation: 'wsZzz 3.4s ease-out infinite' }}>z</text>
                <text x="146" y="50" fontFamily="DM Sans, sans-serif" fontSize="16"
                      fontWeight="700" fill={p.line} opacity="0"
                      style={{ animation: 'wsZzz 3.4s ease-out 1.2s infinite' }}>z</text>
              </g>
            )}
          </svg>
          )}

          {poked && (
            <span style={{ position: 'absolute', right: 10, top: 10, background: '#fff',
                           border: `1px solid ${p.edge}`, borderRadius: 10, padding: '6px 11px',
                           fontSize: 12, fontWeight: 600, color: 'var(--text)',
                           boxShadow: '0 2px 8px rgba(14,27,71,.1)',
                           animation: 'wsPop .35s ease-out both' }}>
              {POKE[mood]}
            </span>
          )}
        </button>

        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>{greeting}</div>
          <div style={{ fontSize: 14.5, color: 'var(--text2)', marginTop: 7, maxWidth: '52ch' }}>
            {line}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 10, marginTop: 16 }}>
            {cards.map(([label, value, note, colour, page]) => (
              <button key={label} type="button" onClick={() => { close(); go && go(page); }}
                style={{ font: 'inherit', textAlign: 'left', cursor: 'pointer', minHeight: 44,
                         background: 'rgba(255,255,255,.72)', border: `1px solid ${p.edge}`,
                         borderLeft: `3px solid ${colour}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: colour,
                              fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text)' }}>{label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{note}</div>
              </button>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <button type="button" onClick={close} className="btn btn-secondary btn-sm">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
